import React, { useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { useScrollToTop } from '@react-navigation/native';
import { View } from 'react-native';
import Animated, { useAnimatedRef, useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import InfoCard from 'design-system/InfoCard';
import { AppView, NavigationHeader } from 'design-system';
import { floatingButtonArea } from 'design-system/FloatingButton';
import { ExpandableFloatingBarSpacer } from 'design-system/ExpandableFloatingBar';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppState from 'hooks/useAppState';
import useIsTablet from 'hooks/useIsTablet';
import { useAppSelector } from 'store/hooks';
import useStatusBar from 'hooks/useStatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectFeatureFlag } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import AvatarFeed from 'features/feed/components/avatar-feed/AvatarFeed';
import WealthISAAllowanceInfo from 'features/wealth/components/ISAAllowanceInfo';
import { selectHasCashIsaPot } from 'features/pots/reducers/selectors';
import { selectIsInvestWalkthroughActive } from 'reducers/selectors/walkthrough';
import InvestWalkthrough from 'features/feed/components/walkthrough/InvestWalkthrough';
import InvestHeaderOverlay from 'features/feed/components/walkthrough/invest/InvestHeaderOverlay';
import useInvestWalkthroughLayout from 'features/feed/components/walkthrough/useInvestWalkthroughLayout';
import Section from './Section';
import DepositCTA from './DepositCTA';
import * as config from '../constants';
import TopUpButton from './TopUpButton';
import AutoInvestCard from './AutoInvestCard';
import UpgradeFeesCard from './UpgradeFeesCard';
import InvestUpgradeCTA from './InvestUpgradeCTA';
import TopUpSecclAccount from './TopUpSecclAccount';
import PriceAlertSection from './PriceAlertSection';
import ISAAllowanceInfo from './ISAAllowanceInfo';
import JISAAllowanceInfo from './JISAAllowanceInfo';
import ProductTransferCard from './ProductTransferCard';
import useRealTimePrice from '../hooks/useRealTimePrice';
import FloatingSearchButton from './FloatingSearchButton';
import MostHeldStocksSection from './MostHeldStocksSection';
import PortfolioScreenHeader from './PortfolioScreenHeader';
import { InvestPortfolioContext } from '../context/portfolio';
import LimitStopOrdersSection from './LimitStopOrdersSection';
import FSCSCompensationInfoView from './FSCSCompensationInfoView';
import PendingActivitiesSection from './PendingActivitiesSection';
import usePortfolioScreenData from '../hooks/usePortfolioScreenData';
import PortfolioHeader from './PortfolioHeader';
import { useAlpacaClosingInfo } from '../hooks/useAlpacaClosingInformation';
import { selectHasAutoInvest, selectShowISATransfers } from '../reducers/selectors';
import InvestUsageExpandableFloatingBar from './InvestUsageExpandableFloatingComponent/InvestUsageExpandableFloatingBar';
import { isWeb } from '../../../constants';
const emptyPositions = [];
const emptyWatchlists = [];
const emptyQueuedOrders = [];
const emptyPendingOrders = [];
const emptyRecurringBuys = [];
const getTitle = (tradingAccount) => {
    switch (tradingAccount?.accountType) {
        case 'GIA':
            return 'General Investments';
        case 'ISA':
            return 'Stocks & Shares ISA';
        case 'JISA':
            return `${tradingAccount.child.firstName}’s JISA`;
        default:
            return '';
    }
};
const Portfolio = ({ isInTab }) => {
    useStatusBar('dark-content', false);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const styles = useStyles(styleSet);
    const marginBottom = useMarginBottom('marginBottom', -rem(8));
    const isTablet = useIsTablet();
    const hasStockAlerts = useAppSelector((store) => selectFeatureFlag(store, 'stock_alerts').value);
    const hasAutoInvest = useAppSelector(selectHasAutoInvest);
    const scrollY = useSharedValue(0);
    const portfolioHeaderRef = useRef(null);
    const lastUpdateTimestamp = useRef(new Date().getTime());
    const showISATransfers = useAppSelector(selectShowISATransfers);
    const hasCashIsaPot = useAppSelector(selectHasCashIsaPot);
    const [data, portfolioContextData, update, , { selectedPositionFilter, setSelectedPositionFilter }] = usePortfolioScreenData();
    const isTradingAccountISA = data.tradingAccount?.accountType === 'ISA';
    const isTradingAccountJISA = data.tradingAccount?.accountType === 'JISA';
    const AlpacaClosingInfo = useAlpacaClosingInfo();
    const walkthroughInProgress = useAppSelector(selectIsInvestWalkthroughActive);
    useLayoutEffect(() => {
        navigation.setOptions({
            ...(!isInTab ? { title: getTitle(data.tradingAccount) } : {}),
            headerTintColor: walkthroughInProgress ? colors.elements.primaryOnModal : colors.text.primary,
        });
    }, [colors.elements.primaryOnModal, colors.text.primary, data.tradingAccount, isInTab, walkthroughInProgress]);
    useRealTimePrice();
    const { itemPositions, scrollHeight, scrollLayoutHeight, onContentSizeChange, onLayout, onScrollLayout } = useInvestWalkthroughLayout();
    useEffect(() => {
        Portfolio.update = () => {
            update();
            portfolioHeaderRef.current?.updateCharts();
        };
        return () => {
            Portfolio.update = () => { };
        };
    }, [update]);
    const appStateChangeCallbacks = useMemo(() => ({
        didBecomeActive: () => {
            const now = new Date().getTime();
            if (now - lastUpdateTimestamp.current >= config.PORTFOLIO_UPDATE_MIN_DELAY) {
                update(false);
                portfolioHeaderRef.current?.updateCharts();
                lastUpdateTimestamp.current = now;
            }
        },
    }), [update]);
    useAppState(appStateChangeCallbacks);
    const scrollRef = useAnimatedRef();
    useScrollToTop(scrollRef);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isInTab || isWeb);
    const { hideTabNavigation } = useAppSelector(selectTabsPriority);
    const headerLeft = useMemo(() => !isInTab || (isTablet && !hideTabNavigation) ? null : (<View style={styles.avatar}>
          <AvatarFeed />
        </View>), [hideTabNavigation, isInTab, isTablet, styles.avatar]);
    const onDiscoverButtonLayout = useCallback((e) => {
        onLayout('discoverButton', e);
    }, [onLayout]);
    const onYourInvestmentsLayout = useCallback((e) => {
        onLayout('onYourInvestmentsLayout', e);
    }, [onLayout]);
    const onNavigationHeaderLayout = useCallback((e) => {
        onLayout('portfolioScreenHeader', e);
    }, [onLayout]);
    const onCashBalanceCardLayout = useCallback((e) => {
        onLayout('cashBalanceCard', e);
    }, [onLayout]);
    const onAutoinvestCardLayout = useCallback((e) => {
        onLayout('autoinvestCard', e);
    }, [onLayout]);
    const onTransferAnIsaLayout = useCallback((e) => {
        onLayout('transferAnIsaCard', e);
    }, [onLayout]);
    const showInvestUsageExpandableFloatingBar = !data.isAlpacaAccount && !data.hasTraded && !walkthroughInProgress;
    const handleInvestUsageExpandableFloatingBarPress = useCallback(() => {
        navigation.navigate('StartInvestChooseStrategy');
    }, []);
    const contentContainerStyle = useMemo(() => [
        {
            // eslint-disable-next-line no-nested-ternary
            paddingBottom: data.isAlpacaAccount
                ? SCREEN_BOTTOM_PADDING
                : !showInvestUsageExpandableFloatingBar && !isTablet
                    ? floatingButtonArea
                    : 0,
        },
        paddingHorizontalStyle,
    ], [data.isAlpacaAccount, showInvestUsageExpandableFloatingBar, isTablet, paddingHorizontalStyle]);
    return (<InvestPortfolioContext.Provider value={portfolioContextData}>
      <AppView withFrame={false}>
        <NavigationHeader withFrame isInTab={isInTab} scrollY={scrollY} noPaddingTop={isInTab} leftContent={headerLeft} overlay={InvestHeaderOverlay} onLayout={onNavigationHeaderLayout} rightContent={<PortfolioScreenHeader isInTab={isInTab} isAlpacaAccount={data.isAlpacaAccount}/>}/>
        <Animated.ScrollView ref={scrollRef} scrollEventThrottle={16} onScroll={scrollHandler} onLayout={onScrollLayout} showsVerticalScrollIndicator={false} scrollEnabled={!walkthroughInProgress} onContentSizeChange={onContentSizeChange} contentContainerStyle={contentContainerStyle}>
          {!!AlpacaClosingInfo && !!data.isAlpacaAccount && (<>
              <Spacer h={8}/>
              <InfoCard>{AlpacaClosingInfo}</InfoCard>
              <Spacer h={8}/>
            </>)}
          {data.showPortfolioChart || !data.isAlpacaAccount ? (<>
              <PortfolioHeader isInTab={!!isInTab} ref={portfolioHeaderRef} hasInvestments={!!data?.positions?.length}/>
              {data.isAlpacaAccount ? (<View style={styles.balanceContainer} onLayout={onCashBalanceCardLayout}>
                  <TopUpButton />
                </View>) : (<Spacer h={8}/>)}
            </>) : (<DepositCTA />)}
          {!!isTradingAccountJISA && <JISAAllowanceInfo />}
          {!!isTradingAccountISA &&
            (hasCashIsaPot ? (<View style={styles.isaAllowanceContainer}>
                <WealthISAAllowanceInfo fromInvest/>
                <Spacer.H16 />
              </View>) : (<ISAAllowanceInfo />))}
          {data.isAlpacaAccount ? null : (<View onLayout={onCashBalanceCardLayout}>
              <TopUpSecclAccount />
            </View>)}

          {!!hasAutoInvest && (<View onLayout={onAutoinvestCardLayout}>
              <AutoInvestCard accountType={data.tradingAccount?.accountType ?? 'GIA'}/>
            </View>)}
          {!!data.positions?.length && !!data.isAlpacaAccount && <InvestUpgradeCTA />}
          {!data.isAlpacaAccount && <PendingActivitiesSection />}
          {!!(!data.isAlpacaAccount || data.positions?.length) && (<View onLayout={onYourInvestmentsLayout}>
              <Section showFilter={data.isAlpacaAccount} renderTotalCash={!data.isAlpacaAccount} positions={data.positions ?? emptyPositions} selectedPositionFilter={selectedPositionFilter} setSelectedPositionFilter={setSelectedPositionFilter}/>
            </View>)}
          {!!isTradingAccountISA && !!showISATransfers && (<View onLayout={onTransferAnIsaLayout}>
              <ProductTransferCard />
            </View>)}
          {data.isAlpacaAccount ? (<>
              <Section orders={data.queuedOrders ?? emptyQueuedOrders}/>
              <Section pending orders={data.pendingOrders ?? emptyPendingOrders}/>
              <LimitStopOrdersSection orders={data.limitStopOrders}/>
              <Section watchlists={data.watchlists ?? emptyWatchlists}/>
              {!!hasStockAlerts && <PriceAlertSection pendingStockAlerts={data.pendingStockAlerts}/>}
              <Section recurringBuys={data.recurringBuys ?? emptyRecurringBuys}/>
            </>) : null}
          {!data.isAlpacaAccount && !!data.positionsLoaded && !data.positions?.length && <MostHeldStocksSection />}
          {!data.isAlpacaAccount && <UpgradeFeesCard />}
          {data.isAlpacaAccount ? (<>
              <Spacer h={34}/>
              <View style={styles.blurbContainer}>
                <Text TextThin-12 Gray centered>
                  {config.LEGAL_NOTICE}
                </Text>
              </View>
            </>) : (<>
              <Spacer h={16}/>
              <FSCSCompensationInfoView />
              {!!showInvestUsageExpandableFloatingBar && (<>
                  <Spacer.H16 />
                  <ExpandableFloatingBarSpacer />
                </>)}
            </>)}
          <InvestWalkthrough isInTab={false} scrollRef={scrollRef} scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>
        </Animated.ScrollView>
        {!data.isAlpacaAccount && (<View onLayout={onDiscoverButtonLayout}>
            <FloatingSearchButton hide={showInvestUsageExpandableFloatingBar} source="portfolio" style={marginBottom}/>
          </View>)}
      </AppView>
      {!!showInvestUsageExpandableFloatingBar && (<InvestUsageExpandableFloatingBar onPress={handleInvestUsageExpandableFloatingBarPress}/>)}
    </InvestPortfolioContext.Provider>);
};
Portfolio.update = () => { };
const styleSet = createStyleSheets(() => ({
    blurbContainer: {
        paddingHorizontal: rem(22),
    },
    avatar: {
        position: 'absolute',
        left: rem(16),
    },
    balanceContainer: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
    },
    isaAllowanceContainer: {
        paddingHorizontal: rem(16),
    },
}));
export default Portfolio;
